<template>
  <div class="mpf-grid-row">
    <div class="mpf-grid-column-two-thirds">
      <p v-if="apiLoading && !appointment" class="mpf-body" role="status">
        Loading your appointment…
      </p>

      <template v-else-if="appointment">
        <MpfSummaryList :rows="summaryRows" />

        <!-- The endpoint says the appointment exists but is no longer cancellable.
             That is a state of this page, not a failed request, so it renders here
             rather than bouncing to the error page. -->
        <MpfCareCard v-if="!canCancel" variant="non-urgent" :heading="cannotCancelTitle">
          <p>{{ cannotCancelMessage }}</p>
        </MpfCareCard>

        <MpfForm
          v-else
          :fields="formAppointmentCancellationConfirm.fields"
          :form-errors="formErrors"
          submit-label="Cancel appointment"
          submit-variant="warning"
          @submit="handleSubmit"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, onMounted, ref } from 'vue';
  import { storeToRefs } from 'pinia';
  import { useRouter } from 'vue-router';

  import MpfForm from '../components/MpfForm/MpfForm.vue';
  import MpfSummaryList from '../components/MpfSummaryList/MpfSummaryList.vue';
  import MpfCareCard from '../components/MpfCareCard/MpfCareCard.vue';
  import type { FormError } from '../components/MpfForm/MpfForm.props';
  import { formAppointmentCancellationConfirm } from '../../data/forms';
  import { useAppointmentCancellationStore } from '../../stores/useAppointmentCancellationStore';
  import { useErrorStore } from '../../stores/useErrorStore';
  import { errorMessageMeta, errorMessages } from '../../data/errors/errors.data';
  import { useUrlSegments } from '../composables/useUrlSegments';
  import { useAppointmentSummaryRows } from '../composables/useAppointmentSummaryRows';

  const router = useRouter();
  const { segments, firstSegment } = useUrlSegments();

  const store = useAppointmentCancellationStore();
  const { apiLoading, appointment, canCancel, cannotCancelReason } = storeToRefs(store);

  const errorStore = useErrorStore();
  const formErrors = ref<FormError[]>([]);

  // Access is the link alone — no identity challenge — so the id comes straight
  // from the URL and the endpoint decides what may happen next.
  const appointmentId = computed(() => segments.value[segments.value.length - 1] ?? null);

  const { summaryRows } = useAppointmentSummaryRows(appointment);

  // Copy comes from the same catalogue the error page reads, so a reason shown
  // inline here and one that redirects are never worded differently.
  const cannotCancelTitle = computed(
    () =>
      errorMessageMeta[cannotCancelReason.value ?? '']?.title ??
      'Unable to cancel this appointment',
  );

  const cannotCancelMessage = computed(
    () =>
      errorMessages[cannotCancelReason.value ?? ''] ??
      'This appointment can no longer be cancelled online. Please contact your GP practice.',
  );

  onMounted(async () => {
    if (appointmentId.value) await store.load(appointmentId.value);
  });

  const handleSubmit = async (data: { valid: boolean }) => {
    if (!data.valid) return;

    formErrors.value = [];

    const result = await store.cancel();

    if (!result.ok) {
      errorStore.setErrors(result.errors);
      formErrors.value = [
        {
          name: 'confirmCancellation',
          errors: [
            errorStore.getFirstMessage() ||
              'We could not cancel this appointment. Please contact your GP practice.',
          ],
        },
      ];
      return;
    }

    await router.push(`/${firstSegment.value}/${appointmentId.value}/cancelled`);
  };
</script>
